
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "@/lib/apiFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

function useTokenFromQuery() {
  const [params] = useSearchParams();
  return params.get("token") || "";
}

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
}

const rules = {
  length: (s: string) => s.length >= 8,
  upper:  (s: string) => /[A-Z]/.test(s),
  lower:  (s: string) => /[a-z]/.test(s),
  digit:  (s: string) => /\d/.test(s),
  special:(s: string) => /[^A-Za-z0-9]/.test(s),
};

function scorePassword(pw: string) {
  const checks = {
    length: rules.length(pw),
    upper:  rules.upper(pw),
    lower:  rules.lower(pw),
    digit:  rules.digit(pw),
    special:rules.special(pw),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  const pct = Math.round((passed / 5) * 100);
  return { checks, pct };
}

export default function SetPassword() {
  const token = useTokenFromQuery();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [verify, setVerify] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [showTerms, setShowTerms] = useState(false);

  const { checks, pct } = useMemo(() => scorePassword(password), [password]);
  const allGood = checks.length && checks.upper && checks.lower && checks.digit && checks.special;
  const match = password.length > 0 && password === verify;
  const canSubmit = token && allGood && match && agree && !submitting;
  const { toast } = useToast();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      // Sanctum CSRF cookie (if you’re using it)
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });

      // XSRF header is helpful with Sanctum; DO NOT set Content-Type manually=
      const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
      const xsrfToken = getCookie("XSRF-TOKEN");
      if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

      const res = await fetch("/api/complete-invite", {
        method: "POST",
        headers,
        body: JSON.stringify({ token, password }),
      } as any);

      // If backend returns a Response, ensure ok & try to parse
      let ok = true, data: any = {};
      if (res && typeof (res as Response).json === "function") {
        const r = res as Response;
        ok = r.ok;
        data = ok ? await r.json() : { message: await r.text() };
      } else {
        data = res;
      }
      if (!ok) throw new Error(data?.message || "Failed to complete invite");

      // If backend didn’t auto-login, fallback (optional):
      await apiFetch("/login", { method: "POST", body: JSON.stringify({ email: data.email, password }) } as any);

      toast({
            title: "Success",
            description: "You have successfully set your password",
      });
      navigate("/"); // go to dashboard/home
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Unable to set password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto pt-10 px-4">
      <Card className="border">
        <CardHeader>
          <CardTitle>Set Your Password</CardTitle>
        </CardHeader>
        <CardContent>
          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

          <form onSubmit={submit} className="space-y-4">
            {/* Strength meter */}
            <div className="space-y-1">
              <ul className="text-xs text-red-500 grid grid-cols-2 gap-x-4 gap-y-1">
                <li className={checks.length ? "text-foreground" : ""}>≥ 8 characters</li>
                <li className={checks.upper ? "text-foreground" : ""}>1 uppercase</li>
                <li className={checks.lower ? "text-foreground" : ""}>1 lowercase</li>
                <li className={checks.digit ? "text-foreground" : ""}>1 number</li>
                <li className={checks.special ? "text-foreground" : ""}>1 special character</li>
                <li className={match ? "text-foreground" : ""}>Passwords match</li>
              </ul>
              <div className="h-2 w-full bg-muted rounded overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            <Input
              type="password"
              placeholder="Password"
              name="pw"
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
            />
            <Input
              type="password"
              placeholder="Verify Password"
              name="vpw"
              autoComplete="new-password"
              onChange={(e) => setVerify(e.target.value)}
              required
            />

            {/* Terms */}
            <label className="text-sm flex items-start gap-2">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              <span>
                I agree to the{" "}
                <button type="button" className="text-primary underline" onClick={() => setShowTerms(true)}>
                  Terms of Use
                </button>
              </span>
            </label>

            <Button className="w-full" type="submit" disabled={!canSubmit}>
              {submitting ? "Saving…" : "Set Password & Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Terms modal */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Terms of Use</DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm dark:prose-invert max-h-[65vh] overflow-y-auto">
            {/* Put your real terms here */}
            <p>By using this service, you agree to the following terms…</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
